<template>
  <section>
    <a class="edit-link" @click.prevent="$emit('editDetails')">
      <img src="@/assets/user-account/edit.svg" width="21" height="19" alt=""
    /></a>
    <div v-if="noUserInfo">
      <translate tag="p">
        Save your address, phone number and more and we'll remember them each
        time you book.
      </translate>
    </div>
    <div class="address user-info" v-if="personalDetails.address">
      <img src="@/assets/user-account/home.svg" width="24" height="24" alt="" />
      <span>
        <p v-for="(part, index) in formattedAddress" :key="index">
          {{ part }}
        </p>
        <p v-if="personalDetails.city">
          {{ personalDetails.city }}
        </p>
        <p v-if="personalDetails.county">
          {{ personalDetails.county }}
        </p>
        <p v-if="personalDetails.postcode">
          {{ personalDetails.postcode }}
        </p>
        <p v-if="personalDetails.country">
          {{ personalDetails.country }}
        </p>
      </span>
    </div>

    <div v-if="isTelephoneSet" class="user-info telephone">
      <img
        src="@/assets/images/mobile-icon.svg"
        width="14"
        height="23"
        alt=""
      />
      <p>{{ telephoneDisplay }}</p>
    </div>

    <div
      v-if="
        personalDetails.unitType?.name ||
        personalDetails.dimensionsWidth ||
        personalDetails.dimensionsDepth ||
        personalDetails.carRegistrationNumber
      "
      class="user-info personal-details"
    >
      <img
        v-if="personalDetails.unitType?.id"
        loading="lazy"
        class="category-icon vehicle"
        :src="getCategorySvg(personalDetails.unitType.id)"
        width="23"
        height="18"
        alt=""
      /><img
        v-else-if="
          personalDetails.carRegistrationNumber &&
          personalDetails.carRegistrationNumber !== 'none'
        "
        src="@/assets/user-account/car-front.svg"
        width="24"
        height="auto"
        class="vehicle"
        alt=""
      />
      <p>
        <span v-if="personalDetails.unitType?.name" class="unit-type">
          {{ personalDetails.unitType.name }}
        </span>
        <span
          class="meta-info"
          v-if="
            personalDetails.dimensionsWidth && personalDetails.dimensionsDepth
          "
        >
          {{ $t('Width (metres)') }}
          {{ personalDetails.dimensionsWidth }}
          &times;
          {{ $t('Length (metres)') }}
          {{ personalDetails.dimensionsDepth }}
        </span>
        <span
          v-if="
            personalDetails.carRegistrationNumber &&
            personalDetails.carRegistrationNumber !== 'none'
          "
          class="numberplate"
        >
          {{ personalDetails.carRegistrationNumber }}
        </span>
      </p>
    </div>

    <div
      v-if="personalDetails.namesOfAllPartyMembers"
      class="user-info party-details"
    >
      <img src="@/assets/images/adults.svg" width="16" height="23" alt="" />
      <p class="party-names">
        {{ personalDetails.namesOfAllPartyMembers }}
      </p>
    </div>

    <!-- <p v-if="personalDetails.gender">
      <label>{{ $t('Gender') }}</label>
      <translate v-if="personalDetails.gender === 'male'" class="value">
        Male
      </translate>
      <translate v-else-if="personalDetails.gender === 'female'" class="value">
        Female
      </translate>
    </p> -->
  </section>
</template>

<script lang="ts">
import { Component, Prop, Vue, toNative } from '~/utility/pu-class-decorator'
import type { UserPersonalDetails } from '../types'

@Component({
  emits: ['editDetails'],
})
class PersonalDetails extends Vue {
  @Prop()
  personalDetails: UserPersonalDetails

  get noUserInfo() {
    return (
      !this.personalDetails.address &&
      !this.personalDetails.city &&
      !this.personalDetails.county &&
      !this.personalDetails.postcode &&
      !this.personalDetails.country &&
      !this.isTelephoneSet &&
      !this.personalDetails.unitType &&
      !this.personalDetails.dimensionsWidth &&
      !this.personalDetails.dimensionsDepth &&
      !this.personalDetails.carRegistrationNumber &&
      !this.personalDetails.namesOfAllPartyMembers
    )
  }

  get isTelephoneSet() {
    return (
      this.personalDetails.telephone &&
      this.personalDetails.telephone.countryDialingNumber &&
      this.personalDetails.telephone.number
    )
  }

  get telephoneDisplay() {
    const t = this.personalDetails.telephone
    if (!t) {
      return ''
    }
    return `+${t.countryDialingNumber} ${t.number}`
  }

  get formattedAddress() {
    return this.personalDetails.address.split(',').map((part) => part.trim())
  }

  // map to category icons
  unitTypes = [
    { id: '14', imgName: 'tent-pitches' },
    { id: '15', imgName: 'trailer-tent' },
    { id: '16', imgName: 'motorhomes' },
    { id: '17', imgName: 'motorhomes' },
    { id: '18', imgName: 'touring-pitches' },
  ]

  getCategorySvg(id: string) {
    const unitType = this.unitTypes.find((unit) => unit.id === id)
    const imgName = unitType?.imgName || 'car-front'
    const imgPath = unitType
      ? `/assets/images/category-icons/${imgName}.svg`
      : `/assets/user-account/${imgName}.svg`

    const glob = import.meta.glob('@/assets/images/category-icons/*.svg', {
      query: '?url',
      eager: true,
    })

    return glob[`${imgPath}`]?.default
  }
}

export default toNative(PersonalDetails)
</script>

<style scoped>
p {
  font-size: var(--body);
}

.edit-link {
  position: absolute;
  top: 0.9em;
  right: 1em;
  opacity: 0.75;
}

.address p {
  margin: 0;
}

.meta-info {
  color: var(--colour-text-light);
  line-height: 1.1;
  text-wrap-style: balance;
  margin-top: 0;
  display: block;
}

.user-info {
  display: grid;
  grid-template-columns: 1.4em 1fr;
  place-items: start start;
  gap: 0.7em;
  margin-bottom: 0.65em;
}

.user-info.telephone {
  place-items: center start;
}

.user-info img {
  place-self: center center;
  filter: brightness(0) opacity(0.35);
}

.party-names {
  white-space: pre-line;
}

.user-info img.vehicle,
.user-info.party-details img,
.user-info.address img {
  place-self: start center;
}

.vehicle {
  margin-top: 0.1em;
}

.numberplate {
  border-radius: 0.3em;
  font-family: helvetica, arial, sans-serif;
  font-weight: 600;
  padding: 2px 0.4em;
  text-transform: uppercase;
  border: 1px solid #dedede;
  display: inline-block;
}

.unit-type {
  display: block;
}

.personal-details p > span:not(.numberplate) {
  margin-bottom: 0.3em;
}
</style>
